import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'
import { extractApiErrorMessage } from '../../../utils/apiError'

// ============================================================================
// TYPES
// ============================================================================

export interface ResubmitPayload {
  requestId: string
  empNuid: string
  appCode?: string // Defaults to "EMP_CURRENT"
  comments?: string
}

export interface ResubmitResponse {
  success: boolean
  message?: string
  timeOffRequestId?: string
  toReqId?: string
  status?: string
}

export interface ResubmitState {
  // Resubmit without change
  resubmitNoChangeLoading: boolean
  resubmitNoChangeError: string | null
  resubmitNoChangeSuccess: boolean
  lastSubmittedRequestId: string | null
}

const initialState: ResubmitState = {
  resubmitNoChangeLoading: false,
  resubmitNoChangeError: null,
  resubmitNoChangeSuccess: false,
  lastSubmittedRequestId: null,
}

// ============================================================================
// ASYNC THUNKS
// ============================================================================

/**
 * Resubmit a denied/returned request without making any changes
 * PUT /time-off-requests/{requestId}/resubmit-no-change
 */
export const resubmitRequestNoChange = createAsyncThunk(
  'resubmit/resubmitRequestNoChange',
  async (payload: ResubmitPayload, { rejectWithValue, signal }) => {
    try {
      const { requestId, empNuid, appCode = 'EMP_CURRENT', comments } = payload
      const url = endpoints.requester.resubmitRequestNoChange(requestId)
      const body = {
        empNuid,
        appCode,
        ...(comments && { comments }),
      }
      const response = await api.put(url, body, { signal })
      return response.data as ResubmitResponse
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(
          error,
          'Failed to resubmit request without changes'
        )
      )
    }
  }
)

// ============================================================================
// SLICE DEFINITION
// ============================================================================

const resubmitSlice = createSlice({
  name: 'resubmit',
  initialState,
  reducers: {
    clearResubmitNoChangeState: (state) => {
      state.resubmitNoChangeLoading = false
      state.resubmitNoChangeError = null
      state.resubmitNoChangeSuccess = false
    },
    clearAllResubmitState: (state) => {
      state.resubmitNoChangeLoading = false
      state.resubmitNoChangeError = null
      state.resubmitNoChangeSuccess = false
      state.lastSubmittedRequestId = null
    },
  },
  extraReducers: (builder) => {
    // Resubmit without change
    builder.addCase(resubmitRequestNoChange.pending, (state) => {
      state.resubmitNoChangeLoading = true
      state.resubmitNoChangeError = null
      state.resubmitNoChangeSuccess = false
    })
    builder.addCase(resubmitRequestNoChange.fulfilled, (state, action) => {
      state.resubmitNoChangeLoading = false
      state.resubmitNoChangeSuccess = true
      state.resubmitNoChangeError = null
      state.lastSubmittedRequestId = action.meta.arg.requestId
    })
    builder.addCase(resubmitRequestNoChange.rejected, (state, action) => {
      state.resubmitNoChangeLoading = false
      state.resubmitNoChangeSuccess = false
      state.resubmitNoChangeError = action.payload as string
    })
  },
})

export const { clearResubmitNoChangeState, clearAllResubmitState } =
  resubmitSlice.actions
export default resubmitSlice.reducer
